import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';

import type { Backend } from 'archive/types';
import { FILESYSTEM_TYPE, S3_TYPE, GCS_TYPE } from 'archive/ArchiveStore';
import { FormikFormGroup } from 'components/common';

type Props = {
  checkOutputPath: (path: string) => Promise<{ result: string }>;
  noPreview?: boolean;
};

const StyledDiv = styled.div`
  margin: 10px 0;
`;

const ArchiveBackendOutputPathFieldGroup = ({ checkOutputPath, noPreview = false }: Props) => {
  const { values: formValues, setFieldError } = useFormikContext<Backend>();
  const [pathCheck, setPathCheck] = useState<string | null>(null);
  const [pathError, setPathError] = useState<string | null>(null);
  const _validateOutputPath = useCallback(
    (path: string) => {
      checkOutputPath(path)
        .then((response) => {
          setPathCheck(response.result);
          setPathError(null);
        })
        .catch((error) => {
          try {
            setPathCheck(null);
            const errorMessage = error.additional.body.message;
            setPathError(errorMessage);

            setFieldError('settings.output_path', errorMessage);
          } catch {
            setPathCheck(null);
            setPathError(error.message);

            setFieldError('settings.output_path', error.message);
          }
        });
    },
    [checkOutputPath, setFieldError],
  );
  const _debouncedValidateOutputPath = debounce(_validateOutputPath, 500);
  const labelText = {
    [S3_TYPE]: 'S3 Output base path',
    [GCS_TYPE]: 'GCS Output base path',
    [FILESYSTEM_TYPE]: 'Output base path',
  };
  const exampleText = {
    [S3_TYPE]: 'S3 Output path preview',
    [GCS_TYPE]: 'GCS Output path preview',
    [FILESYSTEM_TYPE]: 'Example output path',
  };
  const placeholder = {
    [S3_TYPE]: '/data/archives/example',
    [GCS_TYPE]: 'data/archives/example',
    [FILESYSTEM_TYPE]: '/data/archives/example',
  };

  const helpText = {
    [S3_TYPE]: 'Base path where the archives should be stored within the S3 bucket.',
    [GCS_TYPE]: 'Base path where the archives should be stored within the GCS bucket.',
    [FILESYSTEM_TYPE]: 'Base path where the archives should be stored.',
  };

  const getPathError = () =>
    pathError && (
      <span>
        <strong>ERROR:</strong> {pathError}
      </span>
    );

  const _onChange = (event) => {
    _debouncedValidateOutputPath(event.target.value);
  };

  useEffect(() => {
    _validateOutputPath(formValues.settings.output_path);
  }, [formValues, _validateOutputPath]);

  return (
    <StyledDiv>
      <FormikFormGroup
        name="settings.output_path"
        placeholder={placeholder[formValues.settings.type]}
        label={<span>{labelText[formValues.settings.type]}</span>}
        required={formValues.settings.type === FILESYSTEM_TYPE}
        help={
          <span>
            {helpText[formValues.settings.type]} Can <strong>only</strong> be set on backend creation and not be changed
            later!
          </span>
        }
        labelClassName=""
        error={getPathError()}
        wrapperClassName=""
        onChange={_onChange}
        disabled={!!formValues?.id}
      />
      {!noPreview && (
        <span>
          <strong>{exampleText[formValues.settings.type]}:</strong>
          <code>{pathCheck}</code>
        </span>
      )}
    </StyledDiv>
  );
};

export default ArchiveBackendOutputPathFieldGroup;
